import { useState, useEffect, useCallback } from 'react';
import { useDuckDB } from '../context/LazyDuckDBContext';
import * as yaml from 'js-yaml';

export interface KpiMetadata {
  id: string;
  name: string;
  description: string;
  fullDescription: string;
  isDividedByGender: boolean;
  operatingArea?: string;
  perspective?: string;
  hasOuData: boolean;
  betterIsHigher: boolean;
  helperText: string;
}

interface KpiConfig {
  kpis: Array<{
    id: string;
    category: string;
    priority: number;
    description: string;
    better_is_higher: boolean;
    helper_text: string;
  }>;
  config: {
    cache: {
      ttl_minutes: number;
    };
    display: {
      show_warnings: boolean;
      max_dropdown_items: number;
    };
  };
}

// Global cache to prevent multiple fetches
let globalKpiCache: KpiMetadata[] | null = null;
let cacheTimestamp: number | null = null;
let fetchPromise: Promise<KpiMetadata[]> | null = null;

// Load KPI config from YAML file
const loadKpiConfig = async (): Promise<KpiConfig> => {
  try {
    const response = await fetch('/config/kpi-config.yml');
    const yamlText = await response.text();
    return yaml.load(yamlText) as KpiConfig;
  } catch (error) {
    console.error('Error loading KPI config:', error);
    // Fallback config with the current hardcoded KPIs
    return {
      kpis: [
        { id: 'N15504', category: 'education', priority: 1, description: 'Meritvärde, åk 9', better_is_higher: true, helper_text: '/ 340' },
        { id: 'N15418', category: 'education', priority: 2, description: 'Betygskriterier, åk 9', better_is_higher: true, helper_text: '%' },
        { id: 'N15436', category: 'education', priority: 3, description: 'Behörighet yrkesprogram, åk 9', better_is_higher: true, helper_text: '%' },
        { id: 'N15482', category: 'education', priority: 4, description: 'Engelska E, åk 6', better_is_higher: true, helper_text: '%' }
      ],
      config: {
        cache: { ttl_minutes: 60 },
        display: { show_warnings: true, max_dropdown_items: 50 }
      }
    };
  }
};

export const useKpiMetadata = () => {
  const [kpiMetadata, setKpiMetadata] = useState<KpiMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { query, ready } = useDuckDB();

  const fetchKpiMetadata = useCallback(async (): Promise<KpiMetadata[]> => {
    // Load config first
    const config = await loadKpiConfig();
    
    // Check if we have valid cached data
    const now = Date.now();
    const cacheTtl = config.config.cache.ttl_minutes * 60 * 1000; // Convert to milliseconds
    
    if (globalKpiCache && cacheTimestamp && (now - cacheTimestamp) < cacheTtl) {
      return globalKpiCache;
    }

    // If there's already a fetch in progress, wait for it
    if (fetchPromise) {
      return fetchPromise;
    }

    // Start new fetch
    fetchPromise = (async () => {
      try {
        // Get KPI IDs from config
        const kpiIds = config.kpis.map(k => k.id);
        const kpiIdsString = kpiIds.map(id => `'${id}'`).join(',');

        // Get KPI metadata from gold table (optimized for frontend)
        const sql = `
          SELECT 
            kpi_id,
            title,
            description,
            is_divided_by_gender,
            operating_area,
            perspective,
            has_ou_data
          FROM gold_kpi_metadata
          WHERE kpi_id IN (${kpiIdsString})
          ORDER BY kpi_id
        `;

        const dbResults = await query<Array<{
          kpi_id: string;
          title: string;
          description: string;
          is_divided_by_gender: boolean;
          operating_area?: string;
          perspective?: string;
          has_ou_data: boolean;
        }>>(sql);

        // Map the results to our interface
        const metadata: KpiMetadata[] = dbResults.map(dbKpi => {
          const configKpi = config.kpis.find(c => c.id === dbKpi.kpi_id);
          
          return {
            id: dbKpi.kpi_id,
            name: dbKpi.title || configKpi?.description || 'Unknown KPI',
            description: dbKpi.description || configKpi?.description || 'No description available',
            fullDescription: dbKpi.description || configKpi?.description || 'No description available',
            isDividedByGender: dbKpi.is_divided_by_gender || false,
            operatingArea: dbKpi.operating_area,
            perspective: dbKpi.perspective,
            hasOuData: dbKpi.has_ou_data || false,
            betterIsHigher: configKpi?.better_is_higher || true,
            helperText: configKpi?.helper_text || ''
          };
        });

        // Sort by priority from config
        const sortedMetadata = metadata.sort((a, b) => {
          const aPriority = config.kpis.find(c => c.id === a.id)?.priority || 999;
          const bPriority = config.kpis.find(c => c.id === b.id)?.priority || 999;
          return aPriority - bPriority;
        });

        // Update global cache
        globalKpiCache = sortedMetadata;
        cacheTimestamp = now;

        return sortedMetadata;
      } catch (err) {
        console.error('Error fetching KPI metadata:', err);
        throw err;
      } finally {
        fetchPromise = null;
      }
    })();

    return fetchPromise;
  }, [query]);

  const loadKpiMetadata = useCallback(async () => {
    if (!ready) return;

    setLoading(true);
    setError(null);

    try {
      const metadata = await fetchKpiMetadata();
      setKpiMetadata(metadata);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load KPI metadata';
      setError(errorMessage);
      console.error('Error loading KPI metadata:', err);
    } finally {
      setLoading(false);
    }
  }, [ready, fetchKpiMetadata]);

  // Load metadata when component mounts or database becomes ready
  useEffect(() => {
    loadKpiMetadata();
  }, [loadKpiMetadata]);

  // Helper function to get KPI info by ID
  const getKpiInfo = useCallback((kpiId: string): KpiMetadata | undefined => {
    return kpiMetadata.find(k => k.id === kpiId);
  }, [kpiMetadata]);

  // Helper function to get KPI options for dropdown
  const getKpiOptions = useCallback(() => {
    return kpiMetadata.map(k => ({
      id: k.id,
      name: k.name
    }));
  }, [kpiMetadata]);

  // Helper function to clear cache (useful for development)
  const clearCache = useCallback(() => {
    globalKpiCache = null;
    cacheTimestamp = null;
    fetchPromise = null;
  }, []);

  return {
    kpiMetadata,
    loading,
    error,
    getKpiInfo,
    getKpiOptions,
    clearCache,
    refetch: loadKpiMetadata
  };
};

export default useKpiMetadata;
